import { navIcons, railIcons } from '../constants/assets';
import { dashboardRoutes } from '../constants/routes';
import type { NavItem, NavSection } from '../types';

/**
 * Collapsed rail — upper group. The design draws these as icon-only chips, using a
 * filled dashboard mark for the active item and its own calendar glyph.
 */
export const primaryNav: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: railIcons.dashboard,
    glyph: { width: 19, height: 18 },
    path: dashboardRoutes.overview,
  },
  {
    id: "my-bookings",
    label: "Bookings",
    icon: railIcons.calendar,
    glyph: { width: 19, height: 18 },
    path: dashboardRoutes.bookings,
  },
  {
    id: "services",
    label: "Services",
    icon: navIcons.services,
    glyph: { width: 20, height: 20 },
    mirrored: true,
    path: dashboardRoutes.services,
  },
  {
    id: "vehicle-management",
    label: "Categories, Brands",
    icon: navIcons.categoriesBrands,
    glyph: { width: 23, height: 23 },
    path: dashboardRoutes.vehicleCategoriesBrands,
  },
  {
    id: "driver",
    label: "Driver",
    icon: navIcons.driver,
    glyph: { width: 18, height: 18 },
    path: dashboardRoutes.drivers,
  },
  {
    id: "customers",
    label: "Customers",
    icon: navIcons.profileSettings,
    glyph: { width: 18, height: 18 },
    path: dashboardRoutes.customers,
  },
  {
    id: "payments",
    label: "Payments",
    icon: navIcons.payments,
    glyph: { width: 17, height: 17 },
    mirrored: true,
    path: dashboardRoutes.payments,
  },
  {
    id: "enquiries",
    label: "Enquiries",
    icon: navIcons.enquiries,
    glyph: { width: 18, height: 18 },
    badge: 'dot',
    path: dashboardRoutes.enquiry,
  },
];

/** Collapsed rail — lower group. */
export const secondaryNav: NavItem[] = [
  {
    id: "profile-settings",
    label: "Profile Settings",
    icon: railIcons.profile,
    glyph: { width: 16, height: 16 },
    path: dashboardRoutes.profile,
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: navIcons.notifications,
    glyph: { width: 18, height: 18 },
    mirrored: true,
    badge: 'dot',
    path: dashboardRoutes.notifications,
  },
  {
    id: "logout",
    label: "Log out",
    icon: navIcons.logout,
    glyph: { width: 21, height: 21 },
    path: "/admin-login",
  },
];

/**
 * Expanded panel. The design groups the same destinations under two headings and swaps
 * in outlined variants of the dashboard, bookings and profile glyphs.
 */
export const navSections: NavSection[] = [
  {
    id: "menu",
    label: "Menu",
    headingGap: 21,
    rowGap: 23,
    listWidth: 170,
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: navIcons.dashboard,
        glyph: { width: 16, height: 16 },
        path: dashboardRoutes.overview,
      },
      {
        id: "my-bookings",
        label: "My Bookings",
        icon: navIcons.myBookings,
        glyph: { width: 16, height: 16 },
        path: dashboardRoutes.bookings,
      },
      {
        id: "services",
        label: "Services",
        icon: navIcons.services,
        glyph: { width: 17.21, height: 17.21 },
        mirrored: true,
        path: dashboardRoutes.services,
      },
      {
        id: "vehicle-management",
        label: "Categories, Brands",
        icon: navIcons.categoriesBrands,
        glyph: { width: 23, height: 23 },
        path: dashboardRoutes.vehicleCategoriesBrands,
      },
      {
        id: "driver",
        label: "Driver",
        icon: navIcons.driver,
        glyph: { width: 15, height: 15 },
        path: dashboardRoutes.drivers,
      },
      {
        id: "customers",
        label: "Customers",
        icon: navIcons.profileSettings,
        glyph: { width: 15, height: 15 },
        path: dashboardRoutes.customers,
      },
      {
        id: "payments",
        label: "Payments",
        icon: navIcons.payments,
        glyph: { width: 17, height: 17 },
        mirrored: true,
        path: dashboardRoutes.payments,
      },
      {
        id: "enquiries",
        label: "Enquiries",
        icon: navIcons.enquiries,
        glyph: { width: 18, height: 18 },
        badge: 'dot',
        path: dashboardRoutes.enquiry,
      },
    ],
  },
  {
    id: "general",
    label: "General",
    headingGap: 20,
    rowGap: 24,
    listWidth: 170,
    items: [
      {
        id: "profile-settings",
        label: "Profile Settings",
        icon: navIcons.profileSettings,
        glyph: { width: 16, height: 16 },
        path: dashboardRoutes.profile,
      },
      {
        id: "notifications",
        label: "Notifications",
        icon: navIcons.notifications,
        glyph: { width: 18, height: 18 },
        mirrored: true,
        badge: 'dot',
        path: dashboardRoutes.notifications,
      },
      {
        id: "logout",
        label: "Logout",
        icon: navIcons.logout,
        glyph: { width: 21, height: 21 },
        path: "/admin-login",
      },
    ],
  },
];
